import { findCatThree, handleFlavorFilterChange } from './utils';
import FlavorCatFourButton from 'components/FlavorFilters/FlavorCatFourButton';
import GridScroll from 'components/GridScroll';
import { PlaceholderShimmerBackground } from 'utils/fragments';
import Stack from 'components/Stack';
import styled from 'styled-components';
import { useCatFourFlavorCounts } from 'utils/hooks/useFlavorsCounts';
import { useFetchFlavors } from 'utils/hooks/api';

const Placeholder = styled.div.attrs(({ shimmerWidth = '10000px' }) => ({
    shimmerWidth,
}))`
    height: 38px;
    width: 33%;
    border-radius: ${({ theme }) => theme.borderRadius_sm};
    background: ${({ theme }) => theme.gray_5};
    margin: ${({ $placeholderMargin }) => $placeholderMargin};
    ${({ isLoading }) => (isLoading ? PlaceholderShimmerBackground : null)}
`;

const CatFourFilterList = ({
    filters,
    setFilter,
    isLoading,
    data,
    filterAccessorsAndOptions,
    handleFiltersWithoutCatFours,
    $placeholderMargin,
    gridContainerCss,
}) => {
    const catFourFilters =
        filters?.flavors?.filter(
            (filterOption) =>
                filterOption?.depth >= 3 &&
                !filterOption?.value.includes('all-'),
        ) || [];
    const flavorsQuery = useFetchFlavors();
    const groupedFlavorsOptions =
        flavorsQuery.data?.groupedFlavorsOptions || {};
    const { catFourFlavorCounts } = useCatFourFlavorCounts({
        data,
        isLoading,
        filterAccessorsAndOptions,
        handleFiltersWithoutCatFours,
        catFourFilters,
    });
    if (catFourFilters.length === 0) {
        return null;
    }
    if (isLoading) {
        return (
            <Placeholder
                $placeholderMargin={$placeholderMargin}
                isLoading={isLoading}
            />
        );
    }
    if (
        catFourFlavorCounts === undefined ||
        Object.keys(catFourFlavorCounts).length === 0
    ) {
        return null;
    }
    const catFourGroupedOptions = catFourFilters.flatMap(
        (f) => findCatThree(f, groupedFlavorsOptions)?.catFourList,
    );
    return (
        <div css={gridContainerCss}>
            <GridScroll
                $paddingTop="0px"
                $paddingRight="1px"
                $paddingBottom="0px"
                $paddingLeft="1px"
            >
                <Stack
                    $space="6px"
                    $direction="row"
                    $wrap={false}
                    css={`
                        display: flex;
                        overflow-x: auto;
                        padding-top: -10px;
                    `}
                >
                    {catFourGroupedOptions
                        .filter(
                            (catFour) => catFourFlavorCounts[catFour.value] > 0,
                        )
                        .sort((a, b) => {
                            const countDiff =
                                catFourFlavorCounts[b.value] -
                                catFourFlavorCounts[a.value];
                            if (countDiff !== 0) return countDiff;
                            return a.label.localeCompare(b.label);
                        })
                        .map((catFour) => {
                            const checked = !!filters.flavors.find(
                                (f) => f.value === catFour.value,
                            );
                            return (
                                <FlavorCatFourButton
                                    key={catFour.label}
                                    checked={checked}
                                    onChange={() =>
                                        handleFlavorFilterChange({
                                            option: catFour,
                                            filters,
                                            setFilter,
                                            groupedFlavorsOptions,
                                            isChecked: checked,
                                        })
                                    }
                                >
                                    <span>
                                        {catFour.label}
                                        <span
                                            css={`
                                                font-weight: 500;
                                                margin-left: 0.5rem;
                                                color: ${({ theme }) =>
                                                    theme.gray_600};
                                            `}
                                        >
                                            {catFourFlavorCounts[catFour.value]}
                                        </span>
                                    </span>
                                </FlavorCatFourButton>
                            );
                        })}
                </Stack>
            </GridScroll>
        </div>
    );
};

export default CatFourFilterList;
